import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { connect } from 'react-redux'

import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import BodyText from 'LuxKit/Typography/BodyText'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalBody from 'LuxKit/Modal/ModalBody'
import TextButton from 'LuxKit/Button/TextButton'
import { cancelTerminalPayment, type CancelTerminalPaymentRequest } from 'api/payment'
import { STRIPE_TERMINAL_PAYMENT_TYPE } from 'constants/payment'
import config from 'constants/config'
import { getCustomOfferItemType } from 'checkout/selectors/view/customOffer'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'

const StyledModalContent = styled(ModalContent)`
  min-height: ${rem(200)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(20)};
`

const AnimationWrapper = styled.div`
  color: ${props => props.theme.palette.brand.primary.normal};
  width: ${rem(100)};
  max-width: 100%;
`

const ErrorButton = styled(TextButton)`
  &.kind-primary.variant-default {
    --button-color: ${props => props.theme.palette.neutral.default.eight};
    --button-background-color: ${props => props.theme.palette.messaging.critical.normalBackground};
    --button-border-color: ${props => props.theme.palette.messaging.critical.normalBackground};
    
    --button-disabled-color: ${props => props.theme.palette.neutral.default.eight};
    --button-disabled-background-color: ${props => props.theme.palette.neutral.default.four};
    --button-disabled-border-color: ${props => props.theme.palette.neutral.default.five};
    
    --button-hover-color: ${props => props.theme.palette.neutral.default.eight};
    --button-hover-background-color: ${props => props.theme.palette.messaging.critical.darkBackground};
    --button-hover-border-color: ${props => props.theme.palette.messaging.critical.darkBackground};
    
    --button-active-color: ${props => props.theme.palette.neutral.default.eight};
    --button-active-background-color: ${props => props.theme.palette.messaging.critical.darkBackground};
    --button-active-border-color: ${props => props.theme.palette.messaging.critical.darkBackground};
    
    --button-focus-color: ${props => props.theme.palette.neutral.default.eight};
    --button-focus-border-color: ${props => props.theme.palette.messaging.critical.normalBackground};
    --button-focus-background-color: ${props => props.theme.palette.messaging.critical.normalBackground};
  }
`

interface Props {
  isOpen?: boolean
  message?: string
  className?: string
  fullHeight?: boolean
  paymentProvider?: string
  orderId?: string
  currencyCode?: string
  customOfferType?: string
  terminalReaderId?: string
}

function ProcessingModal(props: React.PropsWithChildren<Props>) {
  const {
    isOpen,
    message,
    className,
    fullHeight,
    paymentProvider,
    orderId,
    currencyCode,
    customOfferType,
    terminalReaderId,
  } = props

  const intl = useIntl()

  const missingInfoMsg = intl.formatMessage({
    id: '2rOku5h/Fn',
    defaultMessage: 'Missing required payment information. Please try again.',
    description: 'Snackbar error when terminal payment cancellation lacks required info',
  })

  const cancellationErrorHeading = intl.formatMessage({
    id: 'IUtlVJXOHA',
    defaultMessage: 'Cancellation Error',
    description: 'Snackbar heading for terminal payment cancellation error',
  })

  const paymentCancelledHeading = intl.formatMessage({
    id: 'kudASeDEhX',
    defaultMessage: 'Payment Cancelled',
    description: 'Snackbar heading when terminal payment is successfully cancelled',
  })

  const unableToCancelHeading = intl.formatMessage({
    id: 'nxJTqHA2ea',
    defaultMessage: 'Unable to Cancel',
    description: 'Snackbar heading when terminal payment cancellation fails',
  })

  const failedCancelMsg = intl.formatMessage({
    id: 'AUijBknxR5',
    defaultMessage: 'Failed to cancel terminal payment',
    description: 'Default error message when terminal payment cancellation throws an exception',
  })

  const cancelButtonLabel = intl.formatMessage({
    id: 'fPIzHXCrO8',
    defaultMessage: 'Cancel Payment',
    description: 'Button to cancel a Stripe terminal payment during processing',
  })

  const handleCancelTerminalPayment = useCallback(async() => {
    if (!terminalReaderId || !orderId || !currencyCode) {
      showSnackbar(missingInfoMsg, 'critical', {
        heading: cancellationErrorHeading,
      })
      return
    }

    try {
      const response = await cancelTerminalPayment({
        orderId,
        readerId: terminalReaderId,
        currency: currencyCode as CancelTerminalPaymentRequest['currency'],
        brand: config.BRAND as CancelTerminalPaymentRequest['brand'],
        customOfferType,
      })

      const kind = response.result.cancelled ? 'success' : 'warning'

      showSnackbar(response.message, kind, {
        heading: response.result.cancelled ? paymentCancelledHeading : unableToCancelHeading,
      })
    } catch(error: any) {
      const errorMessage = error?.message || failedCancelMsg
      showSnackbar(errorMessage, 'critical', {
        heading: cancellationErrorHeading,
      })
    }
  }, [orderId, currencyCode, customOfferType, terminalReaderId, missingInfoMsg, cancellationErrorHeading, paymentCancelledHeading, unableToCancelHeading, failedCancelMsg])

  const isStripeTerminal = paymentProvider === STRIPE_TERMINAL_PAYMENT_TYPE
  const shouldShowCancel = isStripeTerminal && orderId

  return (
    <ModalBase isOpen={isOpen} className={className} height={fullHeight ? 'full' : undefined} tabletHeight="auto">
      <ModalBody>
        <StyledModalContent>
          <AnimationWrapper>
            <LoadingAnimation />
          </AnimationWrapper>
          {message && <BodyText variant="large" align="center">{message}</BodyText>}
          {(shouldShowCancel) && (
            <ErrorButton
              kind="primary"
              onClick={handleCancelTerminalPayment}
              size="medium"
            >
              {cancelButtonLabel}
            </ErrorButton>
          )}
        </StyledModalContent>
      </ModalBody>
    </ModalBase>
  )
}

const mapStateToProps = (state: App.State) => ({
  paymentProvider: state.ui.modal.paymentProvider,
  orderId: state.checkout.orderId,
  currencyCode: state.checkout.cart.currencyCode,
  customOfferType: getCustomOfferItemType(state),
  terminalReaderId: state.checkout.payment.terminalReaderId,
})

export default connect(mapStateToProps)(ProcessingModal)
